import { IChat } from '../../types/IChat';
import { IChatConfig } from '../../types/IChatConfig';
import { IChatMessage, IChatMessage2 } from '../../types/IChatMessage';
import IChatPin from '../../types/IChatPin';
import IChatVisibility from '../../types/IChatVisibility';
import { ITicket } from '../../types/ITicket';
import { IUser } from '../../types/IUser';
import { IUserProfile } from '../../types/IUserProfile';
import { Api } from './BaseApi'

const ChatApi = Api.injectEndpoints({
    endpoints: (build) => ({
        getChats: build.query<IChat[], boolean | void>({
            query: (includeClosed) => `Chat/My/?includeClosed=${includeClosed ? "true" : "false"}`,
            providesTags: ["Chats"],
        }),
        getUser: build.query<IUser, string | void>({
            query: (uid) => `User/${uid}`,
            providesTags: ["GetUser"],
        }),
        getProfile: build.query<IUserProfile, string | void>({
            query: (uid) => {
                if (uid) {
                    return `User/${uid}/Profile/`;
                }
                console.warn('getProfile', uid);
                return "User/Profile/";
            },
            providesTags: ["Profile"],
        }),
        getConfig: build.query<IChatConfig, void>({
            query: () => `Chat/Config`,
            providesTags: ["ChatConfig"],
        }),
        updateChat: build.mutation<IChat, IChat>({
            query: (chat) => ({
                url: `Chat/${chat.ConversationId}/`,
                method: "POST",
                body: chat
            }),
            invalidatesTags: ["Chat", "Chats"],
            async onQueryStarted({ ConversationId, ...chat }, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    ChatApi.util.updateQueryData('getChat', ConversationId, (draft) => {
                        Object.assign(draft, chat)
                    })
                )
                try {
                    await queryFulfilled
                } catch {
                    patchResult.undo()
                }
            },
        }),
        acceptChat: build.mutation<unknown, IChat>({
            query: (chat) => ({
                url: `Chat/${chat.ConversationId}/Join`,
                method: "POST"
            }),
            invalidatesTags: ["Chat"]
        }),
        leaveChat: build.mutation<unknown, IChat>({
            query: (chat) => ({
                url: `Chat/${chat.ConversationId}/Leave`,
                method: "POST"
            }),
            invalidatesTags: ["Chat"]
        }),
        closeChat: build.mutation<unknown, IChat>({
            query: (chat) => ({
                url: `Chat/${chat.ConversationId}/Close`,
                method: "POST"
            }),
            invalidatesTags: ["Chat", "Chats"]
        }),
        continueChat: build.mutation<IChat, IChat>({
            query: (chat) => ({
                url: `Chat/Continue`,
                method: "POST",
                body: chat
            }),
            invalidatesTags: ["Chat", "Chats"]
        }),
        getChat: build.query<IChat, string>({
            query: (UId) => `Chat/${UId}`,
            providesTags: ["Chat"],
        }),
        getChatForUser: build.query<IChat, number>({
            query: (Id) => `Chat/ByUser/${Id}`,
            providesTags: ["Chat"],
        }),
        getMessages: build.query<IChatMessage[], string>({
            query: (UId) => `Chat/${UId}/Message`,
            providesTags: ["ChatMessages"],
        }),
        getMyVisibility: build.query<IChatVisibility, void>({
            query: () => `Chat/My/Visibility`,
            providesTags: ["ChatMyVisibility"],
        }),
        getPinnedChats: build.query<IChatPin[], void>({
            query: () => `Chat/Pin`,
            providesTags: ["ChatPins"],
        }),
        pinChat: build.mutation<boolean, IChatPin>({
            query: (pin) => {
                return {
                    url: `Chat/${pin.ConversationId}/Pin`,
                    method: "POST",
                    body: pin
                }
            },
            invalidatesTags: ["ChatPins"],
            async onQueryStarted(pin, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    ChatApi.util.updateQueryData('getPinnedChats', undefined, (draft) => {
                        draft.find(d => d.ConversationId === pin.ConversationId) ?? draft.push(pin);
                    })
                )
                try {
                    await queryFulfilled
                } catch {
                    patchResult.undo()
                }
            },
        }),
        unpinChat: build.mutation<boolean, IChatPin>({
            query: (pin) => ({
                url: `Chat/${pin.ConversationId}/Unpin`,
                method: "POST",
                body: pin
            }),
            invalidatesTags: ["ChatPins"],
            async onQueryStarted(pin, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    ChatApi.util.updateQueryData('getPinnedChats', undefined, (pins) => {
                        var pinIndex = pins.indexOf(pins.find(obj => pin.Contact_Id === obj?.Contact_Id && pin.Chat_Id === obj?.Chat_Id))
                        //console.log('unpinChat updateQueryData', pins, pinIndex)
                        //draft.slice(draft.indexOf(draft.find(obj => pin.Contact_Id == obj.Contact_Id && pin.Chat_Id == obj.Chat_Id)), 1);
                        delete pins[pinIndex]
                        //console.log('unpinChat updateQueryData', pins)
                    })
                )
                try {
                    await queryFulfilled
                } catch {
                    patchResult.undo()
                }
            },
        }),
        updateUserVisibility: build.mutation<boolean, IChatVisibility>({
            query: (visData) => ({
                url: `Chat/Visibility`,
                method: "POST",
                body: visData
            }),
            invalidatesTags: ["ChatMyVisibility"],
        }),
        invalidateChatCache: build.mutation<null, void>({
            queryFn: () => ({ data: null }),
            invalidatesTags: ["Chat", "Chats"],
        }),
        createTicket: build.mutation<ITicket, ITicket>({
            query: (info) => ({
                url: "Ticket",
                method: "POST",
                body: info,
            }),
        }),
    }),
    overrideExisting: false,
})


export { ChatApi };